import React from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { FilterBar, StatsCards, ErrorState } from '../../components/Common';
import { StatCard } from '../../components/Common/StatsCards';
import { AssessmentPoint } from '../../types';
import PointsForm from '../../components/HR/PointsForm';

interface AssessmentPointsTabProps {
  assessmentPoints: AssessmentPoint[];
  filteredPoints: AssessmentPoint[];
  pointsLoading: boolean;
  pointsError: unknown;
  pointsSearchTerm: string;
  onPointsSearchChange: (value: string) => void;
  showPointsForm: boolean;
  editingPoint: AssessmentPoint | undefined;
  onAddPoint: () => void;
  onEditPoint: (point: AssessmentPoint) => void;
  onDeletePoint: (point: AssessmentPoint) => void;
  onPointFormSubmit: (data: Partial<AssessmentPoint>) => void;
  onPointFormCancel: () => void;
  onExport: () => void;
  onPrint: () => void;
}

const pointsColumns: TableColumn[] = [
  { key: 'name', label: 'Point Name', sortable: true },
  { key: 'category', label: 'Category', sortable: true, render: (value: string) => (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      {value}
    </span>
  ) },
  { key: 'weight', label: 'Weight', sortable: true, render: (value: number) => (
    <span className="text-sm font-medium text-gray-700">{value}</span>
  ) },
  { key: 'applicableFor', label: 'Applicable For', sortable: true, render: (value: string) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      value === 'candidates' ? 'bg-indigo-100 text-indigo-800' :
      value === 'hiring' ? 'bg-orange-100 text-orange-800' :
      'bg-cyan-100 text-cyan-800'
    }`}>
      {value === 'both' ? 'Both' : value.charAt(0).toUpperCase() + value.slice(1)}
    </span>
  ) },
  { key: 'isActive', label: 'Status', sortable: true, render: (value: boolean) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {value ? 'Active' : 'Inactive'}
    </span>
  ) },
  { key: 'description', label: 'Description', render: (value: string) => (
    <div className="max-w-xs truncate" title={value}>
      {value}
    </div>
  ) }
];

const AssessmentPointsTab: React.FC<AssessmentPointsTabProps> = ({
  assessmentPoints, filteredPoints,
  pointsLoading, pointsError,
  pointsSearchTerm, onPointsSearchChange,
  showPointsForm, editingPoint,
  onAddPoint, onEditPoint, onDeletePoint,
  onPointFormSubmit, onPointFormCancel,
  onExport, onPrint,
}) => {
  const stats: StatCard[] = [
    { label: 'Total Points', value: assessmentPoints.length, color: 'blue' },
    { label: 'Active Points', value: assessmentPoints.filter(p => p.isActive).length, color: 'green' },
    { label: 'For Candidates', value: assessmentPoints.filter(p => p.applicableFor === 'candidates' || p.applicableFor === 'both').length, color: 'purple' },
    { label: 'For Hiring', value: assessmentPoints.filter(p => p.applicableFor === 'hiring' || p.applicableFor === 'both').length, color: 'yellow' },
  ];

  if (showPointsForm) {
    return (
      <>
        <StatsCards stats={stats} />
        <PointsForm
          point={editingPoint}
          onSubmit={onPointFormSubmit}
          onCancel={onPointFormCancel}
        />
      </>
    );
  }

  return (
    <>
      <StatsCards stats={stats} />

      {pointsLoading && (
        <div className="card p-8 text-center text-gray-500">
          Loading assessment points...
        </div>
      )}

      {pointsError && (
        <ErrorState entityName="assessment points" />
      )}

      {!pointsLoading && !pointsError && (
        <>
          <FilterBar
            searchTerm={pointsSearchTerm}
            onSearchChange={onPointsSearchChange}
            searchPlaceholder="Search points..."
            onClearFilters={() => onPointsSearchChange('')}
            totalCount={assessmentPoints.length}
            filteredCount={filteredPoints.length}
            entityName="Assessment Points"
            onAdd={onAddPoint}
            addLabel="Add Point"
            onExport={onExport}
            onPrint={onPrint}
          />
          <Table
            data={filteredPoints}
            columns={pointsColumns}
            onEdit={onEditPoint}
            onDelete={onDeletePoint}
            showAddButton={false}
            searchable={false}
          />
        </>
      )}
    </>
  );
};

export default AssessmentPointsTab;
